import { memo, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { 
  Type, 
  MoreHorizontal,
  Trash2,
  Edit3,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";

const VisualTextNode = memo(({ data, selected, id }) => {
  const [text, setText] = useState(data.config?.text || "");
  const [isEditing, setIsEditing] = useState(false);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    data.onUpdate?.({ text: newText });
  };

  return (
    <div
      className={`group relative rounded-xl transition-all bg-slate-900/90 backdrop-blur ${
        selected 
          ? "ring-2 ring-indigo-500 ring-offset-2 ring-offset-[#000000]" 
          : "ring-1 ring-slate-700/50 hover:ring-slate-600"
      }`}
      style={{ minWidth: 220, maxWidth: 320 }}
    >
      {/* Header */}
      <div className="bg-slate-800/50 px-3 py-2 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <Type className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-medium text-slate-200">
            {data.label || "Text Prompt"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsEditing(!isEditing)}
            className="p-1 hover:bg-slate-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Edit3 className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 hover:bg-slate-700 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-700">
              <DropdownMenuItem 
                onClick={() => data.onDelete?.()} 
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Text Content */}
      <div className="p-3">
        {isEditing ? (
          <textarea
            value={text}
            onChange={handleTextChange}
            onBlur={() => setIsEditing(false)}
            placeholder="Enter your prompt..."
            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-2 text-xs text-slate-200 placeholder-slate-500 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
            rows={4}
            autoFocus
          />
        ) : (
          <div 
            onClick={() => setIsEditing(true)}
            className="text-xs text-slate-300 leading-relaxed cursor-text min-h-[60px]"
          >
            {text || (
              <span className="text-slate-500 italic">Click to add prompt...</span>
            )}
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-5 !h-5 !bg-emerald-500 !border-[3px] !border-slate-900 hover:!bg-emerald-400 hover:!scale-125 !transition-all !cursor-crosshair"
      />
    </div>
  );
});

VisualTextNode.displayName = "VisualTextNode";
export default VisualTextNode;
